import { Box, Button, Stack, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertMessage } from "../../../tenant/tenant-details/tenant-details-types";
import { getSubscriptionPlans } from "../../../../api/tenets";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { routes } from "../../../../constants/routes";
import {
  AlertComponent,
  BreadcrumbComponent,
  SubscriptionCard,
} from "../../../../common/components";
import { bgColors } from "../../../tenant/add-tenant/subscriptions-plans/subscription-plans";
import "./user-change-plan.scss";
import ConfirmationPopup from "../../../../common/components/confirmation-popup/confirmation-popup";
import {
  confirmationDefaultValues,
  ConfirmationPopupTypes,
} from "../../../tenant/tenant-list/tenant-list-helper";
import icons from "../../../../assets/icons/icons";
import {
  downgradeTenantPlanDetails,
  getTenantPlanDetails,
  updateTenantPlanDetails,
} from "../../../../api/settings";
import {
  filterSubscriptionPlans,
  handleButtonText,
} from "../../../tenant/add-tenant/add-new-tenet-helper";
import CustomPlanCard from "../../../../common/components/subscription-card/custom-plan-card";

const tabs = ["Billed Monthly", "Billed Annually", "No Cost"];

const UserChangePlan = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const { activePlanDetails, tenantData } = state || {};
  const [activePlan, setActivePlan] = useState<any>(
    activePlanDetails
      ? { ...activePlanDetails, id: activePlanDetails?.subscription_plan_id }
      : {}
  );
  const [pendingState, setPendingState] = useState<any>(
    tenantData?.pendingPlanDetails
      ? {
          ...tenantData?.pendingPlanDetails,
          id: tenantData?.pendingPlanDetails?.subscription_plan_id,
        }
      : null
  );
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<AlertMessage>({
    open: false,
  });
  const [confirmationModal, setConfirmationModal] =
    useState<ConfirmationPopupTypes>(confirmationDefaultValues);

  const downGradePlan = async () => {
    try {
      setIsLoading(true);
      const payload = {
        subscriptionPlanId: confirmationModal.value,
        planCategory: confirmationModal?.activePlan?.plan_category
          ?.toLowerCase()
          ?.split("_")
          ?.join(" "),
        planType: confirmationModal?.activePlan?.plan_type,
      };
      if (confirmationModal?.activePlan?.plan_category !== "TRIAL") {
        payload.planType =
          confirmationModal?.activePlan?.plan_type?.toLowerCase();
      }
      const resp = await downgradeTenantPlanDetails(payload);
      setSelectedPlan(null);
      setAlertMessage({
        open: true,
        message:
          resp?.data?.data?.message ||
          "Downgrade request email has been sent to the super admin.",
        type: "success",
      });
      getDetails();
      setConfirmationModal(confirmationDefaultValues);
      setIsLoading(false);
    } catch (err: any) {
      const errMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong!";
      setAlertMessage({
        open: true,
        message: errMessage,
        type: "error",
      });
      setIsLoading(false);
      console.error(err);
    }
  };
  const handleCancel = () => {
    setConfirmationModal(confirmationDefaultValues);
  };
  const handleOnOkay = () => {
    downGradePlan();
  };

  const getDetails = async () => {
    try {
      setIsLoading(true);
      const res = await getTenantPlanDetails();
      const data = res?.data?.data;
      setActivePlan({
        ...data?.activePlanDetails,
        plan_name: data?.activePlanDetails?.planName,
        reports_type: data?.activePlanDetails?.reportsType,
        plan_type: data?.activePlanDetails?.plan_type,
        id: data?.activePlanDetails?.subscription_plan_id,
      });
      setPendingState({
        ...data?.pendingPlanDetails,
        id: data?.pendingPlanDetails?.subscription_plan_id,
      });
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.error(e);
    }
  };

  const handleOnClcik = (item: any) => {
    const isPlan = handleButtonText(item, activePlan);
    if (isPlan === "Downgrade") {
      setConfirmationModal({
        ...confirmationDefaultValues,
        isOpen: true,
        title: `Are you sure you want to downgrade your plan?`,
        activePlan: item,
        updatedPartnerDataList: {},
        note: (
          <Typography sx={{ fontSize: "14px" }}>
            By downgrading, you may lose access to some features and face
            reduced limits.
            <br />
            <br />
            You’re about to contact AFP to downgrade your plan. AFP team will
            assist you with the transition.
          </Typography>
        ),
        value: item?.id,
        button: "Contact AFP",
      });
    } else {
      setSelectedPlan(item);
    }
  };

  const updateSubscriptionPlan = async () => {
    try {
      setIsLoading(true);
      const payload = {
        subscriptionPlanId: selectedPlan?.id,
        redirectUrl: "home/settings/myplans",
        planCategory: selectedPlan?.plan_category
          ?.toLowerCase()
          ?.split("_")
          ?.join(" "),
        planType: selectedPlan?.plan_type,
      };
      if (selectedPlan?.plan_category !== "TRIAL") {
        payload.planType = selectedPlan?.plan_type?.toLowerCase();
      }
      const resp = await updateTenantPlanDetails(payload);
      if (resp.data.data.checkoutUrl) {
        const redirectUrl = resp.data.data.checkoutUrl;
        window.location.href = redirectUrl;
      } else {
        setAlertMessage({
          open: true,
          message: resp?.data?.data?.message,
          type: "success",
        });
        setActivePlan(selectedPlan);
      }

      setSelectedPlan(null);
      setIsLoading(false);
    } catch (err: any) {
      const errMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong!";
      setAlertMessage({
        open: true,
        message: errMessage,
        type: "error",
      });
      setIsLoading(false);
      console.error(err);
    }
  };

  const getSubscriptionValues = async () => {
    try {
      setIsLoading(true);
      const response = await getSubscriptionPlans();

      const {
        monthly,
        annually,
        noCost,
        activeTab,
        customMonthly,
        customAnnual,
      } = filterSubscriptionPlans(response?.data?.data, {
        ...activePlan,
        id: activePlan?.subscription_plan_id,
      });
      setActiveTab(activeTab);
      setIsLoading(false);

      setSubscriptionData({
        monthly: monthly,
        annually: annually,
        noCost: noCost,
        customMonthly: customMonthly,
        customAnnual: customAnnual,
      });
    } catch (e) {
      setIsLoading(false);
      console.error(e);
    }
  };
  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  useEffect(() => {
    getSubscriptionValues();
    getDetails();
  }, []);

  const breadcrumbLink = [
    {
      label: "Settings",
      link: routes.PERSONAL_SETTINGS,
    },
    {
      label: "Change Plan",
      handleClick: () => {},
    },
  ];

  return (
    <Box className="user-change-plan-wrapper">
      <Box className="change-plan-header">
        <BreadcrumbComponent breadcrumbLink={breadcrumbLink} />
      </Box>
      <Box className="change-plan-body">
        {pendingState &&
          handleButtonText(pendingState, activePlan) === "Downgrade" && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  background: "rgba(255, 250, 239, 1)",
                  padding: "0px 10px",
                }}
              >
                <img src={icons.ConfirmInfo} alt="info" width={40} />
                <p className="downgrade-text">
                  Your plan downgrade request is under review by AFP team. Our
                  team will contact you soon.
                </p>
              </Box>
            </Box>
          )}
        <Box>
          <Box className="tabs-wrapper">
            <Box className="tabs-container" gap={3}>
              {tabs?.map((tab: string, index: number) => (
                <span
                  className={index === activeTab ? "active" : ""}
                  onClick={() => setActiveTab(index)}
                >
                  {" "}
                  {tab}
                </span>
              ))}
            </Box>
          </Box>

          {activeTab === 0 && (
            <Stack
              spacing={{ xs: 1, sm: 2, md: 4 }}
              direction="row"
              useFlexGap
              sx={{ flexWrap: "wrap", justifyContent: "center" }}
            >
              {subscriptionData?.monthly?.map((item: any, index: number) => (
                <SubscriptionCard
                  item={item}
                  onClick={handleOnClcik}
                  selectedItem={selectedPlan || ""}
                  bgColor={bgColors[index % bgColors.length]}
                  isShowActiveBadge={
                    item?.id === activePlan?.id &&
                    activePlan?.plan_type == item?.plan_type
                  }
                  buttonText={handleButtonText(item, activePlan)}
                  isPending={
                    pendingState &&
                    pendingState?.id === item?.id &&
                    pendingState?.plan_type == item?.plan_type
                  }
                  activePlan={activePlan}
                />
              ))}
              {subscriptionData?.customMonthly?.length > 0 && (
                <CustomPlanCard
                  plans={subscriptionData.customMonthly}
                  activePlan={activePlan}
                  selectedPlan={selectedPlan}
                  onChoosePlan={(plan: any) => {
                    setSelectedPlan(plan);
                  }}
                  showCreateCustomPlanButton={false}
                  pendingPlan={pendingState}
                />
              )}
            </Stack>
          )}
          {activeTab === 1 && (
            <Stack
              spacing={{ xs: 1, sm: 2, md: 4 }}
              direction="row"
              useFlexGap
              sx={{ flexWrap: "wrap", justifyContent: "center" }}
            >
              {subscriptionData?.annually?.map((item: any, index: number) => (
                <SubscriptionCard
                  item={item}
                  onClick={handleOnClcik}
                  selectedItem={selectedPlan || ""}
                  bgColor={bgColors[index % bgColors.length]}
                  isShowActiveBadge={
                    item?.id === activePlan.id &&
                    activePlan?.plan_type == item?.plan_type
                  }
                  buttonText={handleButtonText(item, activePlan)}
                  isPending={
                    pendingState &&
                    pendingState?.id === item?.id &&
                    pendingState?.plan_type == item?.plan_type
                  }
                  activePlan={activePlan}
                />
              ))}
              {subscriptionData?.customAnnual?.length > 0 && (
                <CustomPlanCard
                  plans={subscriptionData.customAnnual}
                  activePlan={activePlan}
                  selectedPlan={selectedPlan}
                  onChoosePlan={(plan: any) => {
                    setSelectedPlan(plan);
                  }}
                  showCreateCustomPlanButton={false}
                  pendingPlan={pendingState}
                />
              )}
            </Stack>
          )}
          {activeTab === 2 && (
            <Stack
              spacing={{ xs: 1, sm: 2, md: 4 }}
              direction="row"
              useFlexGap
              sx={{ flexWrap: "wrap", justifyContent: "center" }}
            >
              {subscriptionData?.noCost?.length ? (
                subscriptionData?.noCost?.map((item: any, index: number) => (
                  <SubscriptionCard
                    item={item}
                    onClick={handleOnClcik}
                    selectedItem={selectedPlan || ""}
                    bgColor={bgColors[index % bgColors.length]}
                    isShowActiveBadge={item?.id === activePlan.id}
                    buttonText={handleButtonText(item, activePlan)}
                    isPending={pendingState && pendingState?.id === item?.id}
                    activePlan={activePlan}
                  />
                ))
              ) : (
                <Box>No Plans Available</Box>
              )}
            </Stack>
          )}
        </Box>
      </Box>
      <Box className="save-user-button-wrapper">
        <Box sx={{ display: "flex", marginRight: "15px", marginTop: "20px" }}>
          <button
            type="button"
            className="cancel-button"
            disabled={!selectedPlan}
            onClick={() => {
              setSelectedPlan(null);
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cancel-button next-button-wrapper"
            disabled={!selectedPlan}
            onClick={updateSubscriptionPlan}
          >
            Save
          </button>
        </Box>
      </Box>
      {alertMessage?.open && (
        <AlertComponent
          message={alertMessage?.message}
          type={alertMessage?.type || "error"}
          open={alertMessage?.open}
          onClose={() => {
            setAlertMessage({ open: false });
          }}
        />
      )}

      {confirmationModal?.isOpen && (
        <ConfirmationPopup
          open={confirmationModal?.isOpen}
          title={confirmationModal?.title}
          note={confirmationModal?.note}
          buttonText={confirmationModal?.button}
          onCancel={handleCancel}
          onOkay={handleOnOkay}
          className="partner-list-confirmation"
          Icon={icons.AlertIcon}
        />
      )}
    </Box>
  );
};

export default UserChangePlan;
